import React, { useCallback, useEffect, useState } from "react";
import config from "../../../config";
import {
    Box, 
    CircularProgress, 
    Typography, 
    Chip, 
    TextField,
    Button
} from "@mui/material";
import GenericTable from "../GenericTable/GenericTable";

// --------------- UTILS ---------------

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "N/A";
        const options = { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
        return date.toLocaleDateString('en-US', options);
    } catch (error) { return "N/A"; }
};

const formatEventType = (type) => {
    if (!type) return "N/A";
    const label = type.toLowerCase().replace(/_/g, " ");

    return (
        <Chip 
            label={label} 
            size="small" 
            variant="outlined" 
            sx={{ 
                fontWeight: 500, 
                color: "#475569", 
                borderColor: "#94a3b8" 
            }} 
        />
    );
};

function GatewayLogsList() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [totalCount, setTotalCount] = useState(0);

    // Filters
    const [search, setSearch] = useState("");

    const logColumns = [
        { id: "created_at", label: "Timestamp", render: (row) => formatDate(row.created_at) },
        { id: "event_type", label: "Event Classification", render: (row) => formatEventType(row.event_type) },
        { id: "store_name", label: "Store Name", render: (row) => row.store_name || "N/A" },
        { id: "email", label: "Client Email", render: (row) => row.email || "N/A" },
        { id: "gateway_subscription_id", label: "Gateway ID", render: (row) => <span style={{ fontFamily: "monospace", color: "#64748b" }}>{row.gateway_subscription_id || "—"}</span> },
    ];

    const fetchLogs = useCallback(async (pageNum = page, rowSize = rowsPerPage, filters = {}) => {
        setLoading(true);
        try {
            const currentSearch = filters.search !== undefined ? filters.search : search;
            const queryParams = new URLSearchParams({
                page: pageNum,
                limit: rowSize,
                search: currentSearch,
            });

            const res = await fetch(`${config.API_BASE_URL_V1}/admin/gateway-logs?${queryParams.toString()}`, {
                headers: config.getHeaders(),
            });
            const json = await res.json();

            if (json.success) {
                setRows(json.data || []);
                setTotalCount(json.pagination?.total_records || 0);
            } else {
                setRows([]);
            }
        } catch (error) {
            console.error("API Error fetching gateway logs:", error);
            setRows([]);
        }
        setLoading(false);
    }, [page, rowsPerPage, search]);

    useEffect(() => {
        fetchLogs(page, rowsPerPage);
    }, [page, rowsPerPage]);

    const handleSearchApply = () => {
        setPage(1);
        fetchLogs(1, rowsPerPage, { search });
    };

    const handleSearchReset = () => {
        setSearch("");
        setPage(1);
        fetchLogs(1, rowsPerPage, { search: "" });
    };

    return (
        <Box sx={{ p: 4 }}>
            {/* Minimal Filter Bar matching Products layout */}
            <Box sx={{ 
                mb: 0, 
                p: 2, 
                backgroundColor: "#ffffff", 
                borderRadius: "8px 8px 0 0", 
                display: "flex", 
                gap: 2, 
                flexWrap: "wrap", 
                alignItems: "center", 
                border: "1px solid #e0e0e0", 
                borderBottom: "none" 
            }}>
                <TextField
                    label="Search"
                    placeholder="Search Gateway ID / Store..."
                    variant="outlined"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchApply()}
                    sx={{ minWidth: 300 }}
                />
                
                <Button 
                    variant="contained" 
                    onClick={handleSearchApply} 
                    sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
                >
                    Search
                </Button>
                
                <Button 
                    variant="outlined" 
                    onClick={handleSearchReset} 
                    sx={{ height: "40px", borderColor: "#1976d2", color: "#1976d2", "&:hover": { backgroundColor: "#f5f5f5" } }}
                >
                    Clear Filters
                </Button>

                <Box sx={{ flexGrow: 1 }} />
                
                <Button 
                    variant="contained" 
                    sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
                >
                    Export
                </Button>
            </Box>

            {!loading && (
                <GenericTable
                    title=""
                    columns={logColumns}
                    rows={rows}
                    isServerSide={true}
                    currentPage={page - 1}
                    rowsPerPage={rowsPerPage}
                    totalCount={totalCount}
                    onPageChange={(_, newPage) => setPage(newPage + 1)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(1);
                    }}
                    showView={false} // ✅ REMOVED: Actions/View column
                    hideExport={true}
                />
            )}

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5, backgroundColor: "#fff", border: "1px solid #e0e0e0" }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
}

export default GatewayLogsList;
